/**
 * 个人中心的数据层:资料、研值余额、研值明细、研究用量、兑换码。
 * 从现有官网 src/features/account、src/features/redemption 搬来,接口与错误分类保持一致。
 */
import {ApiError,NetworkError,apiFetch,type ApiUser} from './api.ts';

export interface CreditsResponse{balance:number;balance_revision:number;unlimited:boolean}

/** 研值流水一条记录;type 已知值:purchase / redeem / checkin / usage / settle / refund / reconciliation。 */
export interface BillingRecord{
 id:string;
 type:string;
 description?:string;
 research_id?:string;usage_ledger_id?:string;request_id?:string;balance_changed?:boolean;
 credits:number;
 waived_credits:number;
 balance_after:number;
 created_at:string;
}

export interface ResearchUsageSummary{
 research_id:string;
 research_name?:string;
 attempt_count:number;
 credits_charged:number;
 waived_credits:number;
 first_used_at:string;
 last_used_at?:string;
}

export interface RecordsPage{records:BillingRecord[];nextCursor:string|null;hasMore:boolean}

export interface AccountOverview{
 user:ApiUser;
 credits:CreditsResponse;
 records:BillingRecord[];
 nextCursor:string|null;
 hasMore:boolean;
 researchUsage:ResearchUsageSummary[];
}

export const RECORDS_PAGE_SIZE=20;

export function fetchCredits(signal?:AbortSignal):Promise<CreditsResponse>{
 return apiFetch<CreditsResponse>('/v1/billing/credits',{auth:true,...(signal?{signal}:{})});
}

export interface RecordsFilter{type?:string;research_id?:string;record_id?:string;start_time?:string;end_time?:string}
export async function fetchRecordsPage(cursor?:string,signal?:AbortSignal,filter:RecordsFilter={}):Promise<RecordsPage>{
 const params=new URLSearchParams({limit:String(RECORDS_PAGE_SIZE)});
 if(cursor)params.set('cursor',cursor);
 for(const [key,value] of Object.entries(filter))if(value)params.set(key,value);
 const result=await apiFetch<{records:BillingRecord[];next_cursor?:string;has_more:boolean}>(`/v1/billing/records?${params.toString()}`,{auth:true,...(signal?{signal}:{})});
 return {records:result.records,nextCursor:result.next_cursor??null,hasMore:result.has_more};
}

export async function fetchResearchUsage(signal?:AbortSignal):Promise<ResearchUsageSummary[]>{
 const result=await apiFetch<{research_usage:ResearchUsageSummary[]}>('/v1/billing/research-usage?limit=100',{auth:true,...(signal?{signal}:{})});
 return result.research_usage;
}

/** 个人中心首屏:资料、余额、第一页明细与研究用量并行加载。 */
export async function loadAccountOverview(signal?:AbortSignal):Promise<AccountOverview>{
 const [user,credits,firstPage,researchUsage]=await Promise.all([
  apiFetch<ApiUser>('/v1/user/me',{auth:true,...(signal?{signal}:{})}),
  fetchCredits(signal),
  fetchRecordsPage(undefined,signal),
  fetchResearchUsage(signal),
 ]);
 return {user,credits,records:firstPage.records,nextCursor:firstPage.nextCursor,hasMore:firstPage.hasMore,researchUsage};
}

/** 合并分页:按 id 去重,保持接口返回顺序。 */
export function mergeRecords(existing:readonly BillingRecord[],incoming:readonly BillingRecord[]):BillingRecord[]{
 const seen=new Set(existing.map(record=>record.id));
 return [...existing,...incoming.filter(record=>!seen.has(record.id)&&seen.add(record.id))];
}

const RECORD_TYPE_LABELS:Record<string,string>={
 purchase:'充值购买',redeem:'兑换码兑换',checkin:'每日签到',usage:'使用消耗',
 settle:'用量结算',refund:'退款',reconciliation:'对账调整',
};
export function recordTypeLabel(type:string):string{return RECORD_TYPE_LABELS[type]??'其他'}

export function formatCredits(credits:number):string{return credits.toLocaleString('zh-CN')}
export function formatCreditsDelta(credits:number):string{return credits>0?`+${formatCredits(credits)}`:formatCredits(credits)}

export function formatDateTime(iso:string):string{
 const date=new Date(iso);
 if(Number.isNaN(date.getTime()))return iso;
 const parts=new Intl.DateTimeFormat('zh-CN',{timeZone:'Asia/Shanghai',year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit',hourCycle:'h23'}).formatToParts(date);
 const part=(type:string)=>parts.find(v=>v.type===type)?.value??'';
 return `${part('year')}-${part('month')}-${part('day')} ${part('hour')}:${part('minute')}`;
}

/** 密码按字节计长,与后端一致。 */
export function passwordByteLength(password:string):number{return new TextEncoder().encode(password).length}

export function setFirstPassword(password:string):Promise<{ok:boolean}>{
 return apiFetch<{ok:boolean}>('/v1/user/password',{auth:true,body:{password}});
}

export const AVATAR_MAX_FILE_BYTES=650_000;
export const AVATAR_ACCEPT='image/png,image/jpeg,image/gif,image/webp';
const ALLOWED_AVATAR_TYPES=new Set(['image/png','image/jpeg','image/jpg','image/gif','image/webp']);

export interface ProfileUpdate{nick_name?:string;avatar_url?:string}

function hasControlCharacter(value:string):boolean{
 return Array.from(value).some(character=>{
  const codePoint=character.codePointAt(0)??0;
  return codePoint<=0x1f||(codePoint>=0x7f&&codePoint<=0x9f);
 });
}

export function normalizeNickname(value:string):string{
 const nickname=value.trim();
 const length=Array.from(nickname).length;
 if(length<1||length>32||hasControlCharacter(nickname))throw new Error('用户名需为 1–32 个字符，且不能包含控制字符');
 return nickname;
}

export function avatarFileToDataUrl(file:File):Promise<string>{
 if(!ALLOWED_AVATAR_TYPES.has(file.type))return Promise.reject(new Error('请选择 JPG、PNG、GIF 或 WebP 图片'));
 if(file.size>AVATAR_MAX_FILE_BYTES)return Promise.reject(new Error('图片不能超过 650 KB'));
 return new Promise((resolve,reject)=>{
  const reader=new FileReader();
  reader.onerror=()=>reject(new Error('图片读取失败，请重新选择'));
  reader.onload=()=>{
   if(typeof reader.result!=='string'||!reader.result.startsWith('data:image/')){reject(new Error('图片格式不正确'));return}
   resolve(reader.result);
  };
  reader.readAsDataURL(file);
 });
}

export function updateCurrentUserProfile(update:ProfileUpdate):Promise<ApiUser>{
 return apiFetch<ApiUser>('/v1/user/me',{auth:true,method:'PATCH',body:update});
}

export interface RedeemSuccess{
 kind:'success';
 campaignName:string;
 rewardCredits:number;
 balance:number;
 balanceRevision:number;
 record:BillingRecord;
 redeemedAt:string;
}
export interface RedeemFailure{kind:'failure';message:string;retryAfterSeconds?:number}
export type RedeemOutcome=RedeemSuccess|RedeemFailure;

/** HTTP 状态 + 业务码 → 稳定页面文案;表外一律按系统异常处理。 */
function describeRedeemError(error:unknown):RedeemFailure{
 if(error instanceof NetworkError)return {kind:'failure',message:error.message};
 if(!(error instanceof ApiError))return {kind:'failure',message:'系统异常，请稍后重试'};
 const {status,code}=error;
 if(status===429)return {kind:'failure',message:'操作过于频繁，请稍后重试',...(error.retryAfterSeconds?{retryAfterSeconds:error.retryAfterSeconds}:{})};
 if(status===400&&code===1001)return {kind:'failure',message:'请求无效，请检查兑换码后重试'};
 if(status===400&&code===1003)return {kind:'failure',message:'兑换码不可用'};
 if(status===400&&code===4010)return {kind:'failure',message:'当前账号为无限额度账号，无需兑换研值'};
 if(status===401)return {kind:'failure',message:'登录已过期，请重新登录后再兑换'};
 if(status===409&&code===4011)return {kind:'failure',message:'你已达到该活动的领取上限'};
 if(status===409&&code===4012)return {kind:'failure',message:'该兑换码已被领取'};
 if(status===409&&code===4013)return {kind:'failure',message:'请求已失效，请重新提交'};
 if(status===409&&code===1001)return {kind:'failure',message:'研值余额已达到上限'};
 if(status===413&&code===1004)return {kind:'failure',message:'请求内容过大'};
 return {kind:'failure',message:'系统异常，请稍后重试'};
}

interface RedeemResponse{
 campaign_display_name:string;
 reward_credits:number;
 balance:number;
 balance_revision:number;
 record:BillingRecord;
}

export interface Redeemer{redeem(rawCode:string,signal?:AbortSignal):Promise<RedeemOutcome>}

/**
 * 幂等键生命周期:新兑换意图生成新键;网络失败重试复用同一个键,保证重试不会重复加研值;
 * 收到 409/4013 废弃旧键;成功后消费掉当前键。键只进 HTTP 头,不暴露给页面。
 */
export function createRedeemer():Redeemer{
 let key:string|null=null;
 let keyForCode:string|null=null;
 return {
  async redeem(rawCode:string,signal?:AbortSignal):Promise<RedeemOutcome>{
   const code=rawCode.trim();
   if(!key||keyForCode!==code){key=crypto.randomUUID();keyForCode=code}
   try{
    const result=await apiFetch<RedeemResponse>('/v1/billing/redeem',{auth:true,idempotencyKey:key,body:{code},...(signal?{signal}:{})});
    key=null;keyForCode=null;
    return {kind:'success',campaignName:result.campaign_display_name,rewardCredits:result.reward_credits,balance:result.balance,balanceRevision:result.balance_revision,record:result.record,redeemedAt:result.record.created_at};
   }catch(error){
    const failure=describeRedeemError(error);
    if(error instanceof ApiError&&error.status===409&&error.code===4013){key=null;keyForCode=null}
    return failure;
   }
  },
 };
}

export interface ResearchPage{researchUsage:ResearchUsageSummary[];nextCursor:string|null;hasMore:boolean}
export async function fetchResearchPage(cursor?:string,signal?:AbortSignal):Promise<ResearchPage>{
 const params=new URLSearchParams({limit:'20'});if(cursor)params.set('cursor',cursor);
 const data=await apiFetch<{research_usage:ResearchUsageSummary[];next_cursor?:string;has_more:boolean}>(`/v1/billing/research-usage?${params}`,{auth:true,signal});
 return {researchUsage:data.research_usage,nextCursor:data.next_cursor??null,hasMore:data.has_more};
}
export function sendPasswordResetCode(phone:string,signal?:AbortSignal){return apiFetch<{ok:boolean}>('/v1/auth/password/reset/code',{body:{phone},signal})}
export function resetPassword(phone:string,code:string,new_password:string,signal?:AbortSignal){return apiFetch<{ok:boolean}>('/v1/auth/password/reset',{body:{phone,code,new_password},signal})}
export function describePasswordError(error:unknown):{message:string;retryAfterSeconds?:number}{
 if(error instanceof ApiError){if(error.status===401)return {message:'验证码错误或已失效，请重新获取'};if(error.status===429)return {message:'操作过于频繁，请稍后重试',retryAfterSeconds:error.retryAfterSeconds};if(error.status===400)return {message:'请检查验证码和密码格式'};if(error.status===503)return {message:'服务繁忙，请稍后重试'}}
 return {message:error instanceof NetworkError?error.message:'操作失败，请稍后重试'};
}
export function newerCredits(current:CreditsResponse|null,incoming:CreditsResponse):CreditsResponse{
 return current&&current.balance_revision>incoming.balance_revision?current:incoming;
}
